import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';
import { useSettings } from '../hooks/useSettings';
import { useTenant } from '../contexts/TenantContext';
import { useT } from '../i18n';
import { OnboardingGuide } from '../components/OnboardingGuide';

// Componente para exibir contador do QR Code
function QRCountdown({ expiresAt }: { expiresAt: Date }) {
  const t = useT();
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = expiresAt.getTime();
      const difference = expiry - now;
      return Math.max(0, Math.ceil(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (timeLeft <= 0) {
    return <span className="text-red-600 text-sm">{t('conn.qrExpired')}</span>;
  }

  return (
    <span className="text-orange-600 text-sm">
      Expires in {timeLeft}s
    </span>
  );
}

type Provider = 'WAHA' | 'EVOLUTION' | 'QUEPASA' | 'META';

interface MetaWebhookInfo {
  callbackUrl: string | null;
  verifyToken: string | null;
}

const EMPTY_META_FIELDS = { phoneNumberId: '', wabaId: '', accessToken: '', appSecret: '' };

function providerLabel(provider: Provider): string {
  switch (provider) {
    case 'EVOLUTION': return 'Evolution API';
    case 'QUEPASA': return 'Quepasa';
    case 'META': return 'WhatsApp Cloud API';
    default: return 'Waha';
  }
}

function ProviderIcon({ provider, className }: { provider: Provider; className: string }) {
  if (provider === 'META') {
    // Sem arquivo de ícone: o selo oficial é só a cor do WhatsApp
    return <span className={`${className} rounded bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center`}>M</span>;
  }
  const src = provider === 'EVOLUTION' ? '/iconeevolutionapi.png' : provider === 'QUEPASA' ? '/iconequepasa.png' : '/iconewaha.png';
  return <img src={src} alt={providerLabel(provider)} className={`${className} object-contain`} />;
}

interface WhatsAppSession {
  name: string; // Nome real usado na API (ex: vendas_c52982e8)
  displayName?: string; // Nome exibido ao usuário (ex: vendas)
  status: 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED';
  provider: Provider;
  metaQualityRating?: string | null;
  qr?: string;
  qrExpiresAt?: Date;
  me?: {
    id: string;
    pushName: string;
  };
  inboxEnabled?: boolean;
  interactiveCampaignEnabled?: boolean;
}


export function WhatsAppConnectionsPage() {
  const t = useT();
  const { settings } = useSettings();
  const { selectedTenantId, loading: tenantLoading } = useTenant();
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionProvider, setNewSessionProvider] = useState<Provider>('WAHA');
  const [metaFields, setMetaFields] = useState(EMPTY_META_FIELDS);
  const [metaWebhook, setMetaWebhook] = useState<MetaWebhookInfo | null>(null);
  const [checkingMeta, setCheckingMeta] = useState<string | null>(null);
  const [interactiveCampaignEnabled, setInteractiveCampaignEnabled] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingQR, setLoadingQR] = useState<string | null>(null);
  const [togglingInbox, setTogglingInbox] = useState<string | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentQRSession, setCurrentQRSession] = useState<WhatsAppSession | null>(null);
  const [createSessionModalOpen, setCreateSessionModalOpen] = useState(false);
  const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);
  const [allowedProviders, setAllowedProviders] = useState<string[]>(['WAHA', 'EVOLUTION', 'QUEPASA', 'META']);

  // Preload das imagens dos provedores para carregamento instantâneo
  useEffect(() => {
    const images = [
      '/iconewaha.png',
      '/iconeevolutionapi.png',
      '/iconequepasa.png'
    ];

    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Carregar provedores permitidos do tenant
  const loadAllowedProviders = async () => {
    try {
      const response = await authenticatedFetch('/api/tenants/allowed-providers');
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.allowedProviders)) {
          setAllowedProviders(data.allowedProviders);
          // Definir o primeiro provedor permitido como padrão
          if (data.allowedProviders.length > 0 && !data.allowedProviders.includes(newSessionProvider)) {
            setNewSessionProvider(data.allowedProviders[0] as Provider);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar provedores permitidos:', error);
    }
  };

  useEffect(() => {
    // Não carregar sessões até que o tenant esteja definido
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    // Carregar provedores permitidos
    loadAllowedProviders();

    // Recarregar sessões quando o tenant mudar
    loadSessions();

    // Iniciar polling a cada 5 segundos
    const interval = setInterval(() => {
      loadSessions(false); // Não mostrar loading durante polling automático
    }, 5000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedTenantId, tenantLoading]); // Recarrega quando o tenant mudar ou terminar de carregar

  // Polling para verificar se a conexão foi estabelecida quando o modal QR está aberto
  useEffect(() => {
    if (!qrModalOpen || !currentQRSession) {
      return;
    }

    const checkConnection = async () => {
      try {
        const response = await authenticatedFetch('/api/waha/sessions');
        if (response.ok) {
          const sessions = await response.json();
          const updatedSession = sessions.find((s: any) => s.name === currentQRSession.name);

          if (updatedSession) {
            console.log(`🔍 Polling - sessão ${currentQRSession.name}:`, {
              provider: updatedSession.provider,
              status: updatedSession.status,
              me: updatedSession.me
            });

            // Se a sessão mudou para WORKING (conectada)
            if (updatedSession.status === 'WORKING') {
              console.log(`✅ Sessão ${currentQRSession.name} conectada! Fechando modal...`);

              // Fechar modal
              setQrModalOpen(false);
              setCurrentQRSession(null);

              // Atualizar lista de sessões imediatamente
              await loadSessions(false);

              // Mostrar notificação de sucesso
              const userName = updatedSession.me?.pushName || 'User';
              toast.success(`WhatsApp connected successfully! Logged in as: ${userName}`);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status da conexão:', error);
      }
    };

    // Verificar imediatamente
    checkConnection();

    // Depois verificar a cada 2 segundos (polling mais frequente)
    const interval = setInterval(checkConnection, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [qrModalOpen, currentQRSession]);

  const loadSessions = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await authenticatedFetch('/api/waha/sessions');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Processar dados das sessões incluindo QR code salvo no banco
      const processedSessions = data.map((session: any) => ({
        name: session.name,
        displayName: session.displayName || session.name,
        status: session.status || 'STOPPED',
        provider: session.provider || 'WAHA',
        me: session.me || null,
        qr: session.qr || null,
        qrExpiresAt: session.qrExpiresAt ? new Date(session.qrExpiresAt) : undefined,
        inboxEnabled: session.inboxEnabled === true,
        interactiveCampaignEnabled: session.interactiveCampaignEnabled === true
      }));

      setSessions(processedSessions);
      if (showLoading) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
      if (showLoading) {
        toast.error(t('conn.errLoad'));
        setLoading(false);
      }
    }
  };

  const createSession = async () => {
    if (!newSessionName.trim()) {
      toast.error(t('conn.errName'));
      return;
    }

    // Validar se as configurações do provedor estão disponíveis
    if (newSessionProvider === 'WAHA' && !settings?.wahaConfigured) {
      toast.error(t('conn.errWaha'));
      return;
    }

    if (newSessionProvider === 'EVOLUTION' && !settings?.evolutionConfigured) {
      toast.error(t('conn.errEvolution'));
      return;
    }

    if (newSessionProvider === 'QUEPASA' && !settings?.quepasaConfigured) {
      toast.error(t('conn.errQuepasa'));
      return;
    }

    const isMeta = newSessionProvider === 'META';
    if (isMeta && Object.values(metaFields).some(value => !value.trim())) {
      toast.error(t('conn.errMetaFields'));
      return;
    }

    setIsCreating(true);
    try {
      const response = await authenticatedFetch('/api/waha/sessions', {
        method: 'POST',
        body: JSON.stringify({
          name: newSessionName.trim(),
          provider: newSessionProvider,
          interactiveCampaignEnabled,
          ...(isMeta ? metaFields : {})
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: t('conn.errUnknown') }));

        // Verificar se é erro de quota
        if (errorData.upgradeRequired || (errorData.message && /\blimite?\b/i.test(errorData.message))) {
          toast.error(errorData.message || t('conn.errQuota'), {
            duration: 6000,
            icon: '⚠️'
          });
          throw new Error(errorData.message);
        }

        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const createdSessionData = await response.json();

      toast.success(`${providerLabel(newSessionProvider)} session created successfully`);
      setNewSessionName('');
      setNewSessionProvider('WAHA');
      setMetaFields(EMPTY_META_FIELDS);

      // Meta: falta só colar a URL do webhook no painel do app
      if (isMeta) {
        for (const warning of createdSessionData.warnings || []) {
          toast.error(warning, { duration: 8000 });
        }
        setMetaWebhook(createdSessionData.webhook || null);
      }

      // Recarregar imediatamente
      await loadSessions();

      // Se for Evolution e veio QR code, abrir modal automaticamente
      if (newSessionProvider === 'EVOLUTION' && createdSessionData.qrcode?.base64) {
        setTimeout(async () => {
          // Buscar o nome real da sessão criada
          const sessions = await authenticatedFetch('/api/waha/sessions').then(r => r.json());
          const createdSession = sessions.find((s: any) => s.name.startsWith(newSessionName.trim()));

          if (createdSession) {
            await openQRModal(createdSession.name);
          }
        }, 1000);
      }

      // Aguardar mais um pouco e recarregar novamente para pegar status atualizado
      setTimeout(() => {
        loadSessions(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao criar sessão:', error);
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      if (!(error instanceof Error && /\blimite?\b/i.test(error.message))) {
        toast.error(error instanceof Error ? error.message : t('conn.errCreate'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const deleteSession = async (sessionName: string) => {
    if (!confirm(`Are you sure you want to remove the session "${sessionName}"?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: t('conn.errUnknown') }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success(t('conn.removed'));
      loadSessions();
    } catch (error) {
      console.error('Erro ao remover sessão:', error);
      toast.error(error instanceof Error ? error.message : t('conn.errRemove'));
    }
  };

  const restartSession = async (sessionName: string) => {
    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/restart`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: t('conn.errUnknown') }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success(t('conn.restarted'));

      // Aguardar um pouco antes de recarregar
      setTimeout(() => {
        loadSessions(false);
      }, 1000);
    } catch (error) {
      console.error('Erro ao reiniciar sessão:', error);
      toast.error(error instanceof Error ? error.message : t('conn.errRestart'));
    }
  };

  // Liga/desliga o recebimento de mensagens (caixa de entrada) sem reiniciar a conexão
  const toggleInbox = async (sessionName: string, enabled: boolean) => {
    setTogglingInbox(sessionName);
    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/inbox`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: t('conn.errUnknown') }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success(enabled ? t('conn.inboxOn') : t('conn.inboxOff'));
      await loadSessions(false);
    } catch (error) {
      console.error('Erro ao alterar caixa de entrada:', error);
      toast.error(error instanceof Error ? error.message : t('conn.errInbox'));
    } finally {
      setTogglingInbox(null);
    }
  };

  // Meta: confere as credenciais guardadas e devolve a conexão para WORKING
  const checkMetaConnection = async (sessionName: string) => {
    setCheckingMeta(sessionName);
    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      toast.success(t('conn.metaChecked'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('conn.errUnknown'), { duration: 8000 });
    } finally {
      setCheckingMeta(null);
      loadSessions(false);
    }
  };

  const openMetaWebhook = async (sessionName: string) => {
    try {
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/meta-webhook`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      setMetaWebhook(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('conn.errUnknown'));
    }
  };

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('conn.copied'));
    } catch {
      toast.error(t('conn.errUnknown'));
    }
  };

  const requestQRCode = async (sessionName: string) => {
    setLoadingQR(sessionName);
    try {
      // Primeiro, iniciar a sessão
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const errorData = await startResponse.json().catch(() => ({ error: t('conn.errUnknown') }));
        throw new Error(errorData.error || `HTTP ${startResponse.status}`);
      }

      toast.success(t('conn.qrRequested'));

      // Aguardar um pouco e abrir modal para mostrar QR
      setTimeout(async () => {
        await openQRModal(sessionName);
        loadSessions(false);
      }, 2000);

    } catch (error) {
      console.error('Erro ao solicitar QR Code:', error);
      toast.error(error instanceof Error ? error.message : t('conn.errQrRequest'));
    } finally {
      setLoadingQR(null);
    }
  };

  const openQRModal = async (sessionName: string) => {
    try {
      // Primeiro, verificar se já temos um QR salvo no banco para esta sessão
      const currentSession = sessions.find(s => s.name === sessionName);

      if (currentSession?.qr && currentSession?.qrExpiresAt && currentSession.qrExpiresAt > new Date()) {
        // Usar QR code já salvo no banco se ainda não expirou
        const sessionWithQR: WhatsAppSession = {
          name: sessionName,
          status: 'SCAN_QR_CODE',
          provider: currentSession.provider,
          qr: currentSession.qr,
          qrExpiresAt: currentSession.qrExpiresAt,
          me: currentSession.me
        };

        setCurrentQRSession(sessionWithQR);
        setQrModalOpen(true);
        return;
      }

      // Primeiro, iniciar a sessão para gerar o QR
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const startError = await startResponse.json();
        toast.error(`Failed to start session: ${startError.error || t('conn.errUnknown')}`);
        return;
      }

      // Aguardar um pouco para a sessão inicializar
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Agora buscar QR através do backend (que vai rotear para API correta)
      const qrResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/auth/qr`);

      if (qrResponse.ok) {
        const qrData = await qrResponse.json();

        if (qrData.qr) {
          const sessionWithQR: WhatsAppSession = {
            name: sessionName,
            status: 'SCAN_QR_CODE',
            provider: currentSession?.provider || 'WAHA',
            qr: qrData.qr,
            qrExpiresAt: qrData.expiresAt ? new Date(qrData.expiresAt) : new Date(Date.now() + 60000),
            me: undefined
          };

          setCurrentQRSession(sessionWithQR);
          setQrModalOpen(true);

          // Recarregar sessões para obter dados atualizados do banco
          setTimeout(() => {
            loadSessions(false);
          }, 1000);
        } else {
          toast.error(t('conn.qrUnavailable'));
        }
      } else {
        const errorData = await qrResponse.json();
        toast.error(errorData.error || t('conn.errQrFetch'));
      }
    } catch (error) {
      console.error('Erro ao buscar QR Code:', error);
      toast.error(t('conn.errQrFetch'));
    }
  };

  const closeQRModal = () => {
    setQrModalOpen(false);
    setCurrentQRSession(null);
    // Recarregar sessões para verificar se conectou
    loadSessions(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'bg-green-100 text-green-800';
      case 'SCAN_QR_CODE':
        return 'bg-yellow-100 text-yellow-800';
      case 'STOPPED':
        return 'bg-gray-100 text-gray-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'WORKING':
        return t('conn.statusConnected');
      case 'SCAN_QR_CODE':
        return t('conn.statusWaitingQr');
      case 'STOPPED':
        return t('conn.statusStopped');
      case 'FAILED':
        return t('conn.statusError');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">{t('conn.loading')}</span>
      </div>
    );
  }

  return (
    <>
      <Header
        title={t('conn.title')}
        subtitle={`${sessions.length} ${sessions.length === 1 ? 'active session' : 'active sessions'}`}
        actions={
          <div className="flex gap-3">
            <button
              onClick={() => setCreateSessionModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
            >
              + New Session
            </button>
            <button
              onClick={() => loadSessions()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-colors"
            >{t('conn.refresh')}</button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        <OnboardingGuide compact />

      {/* Lista de Sessões */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{t('conn.activeSessions')}</h3>
        </div>

        {sessions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">{t('conn.empty')}</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sessions.map((session) => (
              <div key={session.name} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{session.displayName || session.name}</h4>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        session.provider === 'EVOLUTION' ? 'bg-blue-100 text-blue-800' :
                        session.provider === 'QUEPASA' ? 'bg-purple-100 text-purple-800' :
                        session.provider === 'META' ? 'bg-emerald-100 text-emerald-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        <ProviderIcon provider={session.provider} className="w-4 h-4" />
                        {providerLabel(session.provider)}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                        {getStatusText(session.status)}
                      </span>
                    </div>

                    {session.me && (
                      // O número fica visível mesmo com a sessão caída: saber qual linha é esta conexão
                      // não deveria depender de ela estar no ar neste segundo.
                      <p className={`text-sm ${session.status === 'WORKING' ? 'text-gray-600' : 'text-gray-400'}`}>
                        {session.status === 'WORKING' ? t('conn.connectedAs') : t('conn.lastConnectedAs')}{' '}
                        <span className="font-medium" dir="auto">{session.me.pushName}</span>{' '}
                        <span dir="ltr">({session.me.id.replace(/@.*/, '')})</span>
                      </p>
                    )}

                    {session.provider === 'META' && session.metaQualityRating && (
                      <p className="text-sm mt-1 text-gray-600">
                        {t('conn.metaQuality')}:{' '}
                        <span className={`font-medium ${
                          session.metaQualityRating === 'GREEN' ? 'text-emerald-700' :
                          session.metaQualityRating === 'YELLOW' ? 'text-amber-600' :
                          session.metaQualityRating === 'RED' ? 'text-red-600' : 'text-gray-600'
                        }`}>{session.metaQualityRating}</span>
                      </p>
                    )}

                    <p className="text-sm mt-1">
                      {session.inboxEnabled ? (
                        <span className="text-emerald-700">📥 {t('conn.receiving')}</span>
                      ) : (
                        <span className="text-gray-400">{t('conn.notReceiving')}</span>
                      )}
                    </p>
                  </div>

                  <div className="flex gap-2 ml-4">
                    {session.provider === 'META' && (
                      <>
                        {session.status !== 'WORKING' && (
                          <button
                            onClick={() => checkMetaConnection(session.name)}
                            disabled={checkingMeta === session.name}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                          >
                            {checkingMeta === session.name ? t('conn.requesting') : t('conn.metaCheck')}
                          </button>
                        )}
                        <button
                          onClick={() => openMetaWebhook(session.name)}
                          className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
                        >{t('conn.metaWebhookButton')}</button>
                      </>
                    )}
                    {session.status === 'STOPPED' && session.provider !== 'META' && (
                      <button
                        onClick={() => requestQRCode(session.name)}
                        disabled={loadingQR === session.name}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {loadingQR === session.name ? t('conn.requesting') : t('conn.connect')}
                      </button>
                    )}
                    {session.status === 'SCAN_QR_CODE' && (
                      <button
                        onClick={() => openQRModal(session.name)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                      >{t('conn.viewQr')}</button>
                    )}
                    <button
                      onClick={() => toggleInbox(session.name, !session.inboxEnabled)}
                      disabled={togglingInbox === session.name}
                      className={`px-3 py-1 text-white text-sm rounded-md disabled:opacity-50 ${
                        session.inboxEnabled ? 'bg-gray-500 hover:bg-gray-600' : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                      title={session.inboxEnabled ? t('conn.stopReceivingTitle') : t('conn.receiveTitle')}
                    >
                      {togglingInbox === session.name
                        ? t('conn.saving')
                        : session.inboxEnabled ? t('conn.stopReceiving') : t('conn.receiveMessages')}
                    </button>
                    <button
                      onClick={() => restartSession(session.name)}
                      className="px-3 py-1 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700"
                    >{t('conn.restart')}</button>
                    <button
                      onClick={() => deleteSession(session.name)}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                    >{t('conn.remove')}</button>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Criar Sessão */}
      {createSessionModalOpen && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-100 m-4" role="dialog" aria-labelledby="create-session-title">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 id="create-session-title" className="text-2xl font-bold text-gray-900">{t('conn.newSession')}</h2>
              <p className="text-gray-500 mt-2">{t('conn.newSessionSubtitle')}</p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="session-provider" className="block text-sm font-semibold text-gray-700 mb-2">{t('conn.provider')}</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => !isCreating && setProviderDropdownOpen(!providerDropdownOpen)}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm bg-white text-left flex items-center justify-between"
                    disabled={isCreating}
                  >
                    <span className="flex items-center gap-3">
                      <ProviderIcon provider={newSessionProvider} className="w-5 h-5" />
                      {providerLabel(newSessionProvider)}
                    </span>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${providerDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {providerDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setProviderDropdownOpen(false)}
                      />
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg overflow-hidden">
                        {allowedProviders.includes('WAHA') && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewSessionProvider('WAHA');
                              setProviderDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                              newSessionProvider === 'WAHA' ? 'bg-blue-50' : ''
                            }`}
                          >
                            <img src="/iconewaha.png" alt="Waha" className="w-5 h-5 object-contain" />
                            <span className="text-sm">Waha</span>
                          </button>
                        )}
                        {allowedProviders.includes('EVOLUTION') && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewSessionProvider('EVOLUTION');
                              setProviderDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                              newSessionProvider === 'EVOLUTION' ? 'bg-blue-50' : ''
                            }`}
                          >
                            <img src="/iconeevolutionapi.png" alt="Evolution API" className="w-5 h-5 object-contain" />
                            <span className="text-sm">Evolution API</span>
                          </button>
                        )}
                        {allowedProviders.includes('QUEPASA') && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewSessionProvider('QUEPASA');
                              setProviderDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                              newSessionProvider === 'QUEPASA' ? 'bg-blue-50' : ''
                            }`}
                          >
                            <img src="/iconequepasa.png" alt="Quepasa" className="w-5 h-5 object-contain" />
                            <span className="text-sm">Quepasa</span>
                          </button>
                        )}
                        {allowedProviders.includes('META') && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewSessionProvider('META');
                              setProviderDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-50 transition-colors ${
                              newSessionProvider === 'META' ? 'bg-blue-50' : ''
                            }`}
                          >
                            <ProviderIcon provider="META" className="w-5 h-5" />
                            <span className="text-sm">{t('conn.metaName')}</span>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">{t('conn.providerHelp')}</p>
              </div>

              <div>
                <label htmlFor="session-name" className="block text-sm font-semibold text-gray-700 mb-2">{t('conn.sessionName')}</label>
                <input
                  id="session-name"
                  type="text"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder={t('conn.sessionNamePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  disabled={isCreating}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">{t('conn.sessionNameHelp')}</p>
              </div>

              {newSessionProvider === 'META' && (
                <div className="space-y-4 bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                  <p className="text-xs text-gray-700">{t('conn.metaIntro')}</p>
                  {([
                    ['phoneNumberId', 'conn.metaPhoneId', null, 'text'],
                    ['wabaId', 'conn.metaWabaId', null, 'text'],
                    ['accessToken', 'conn.metaToken', 'conn.metaTokenHelp', 'password'],
                    ['appSecret', 'conn.metaAppSecret', 'conn.metaAppSecretHelp', 'password'],
                  ] as const).map(([field, label, help, type]) => (
                    <div key={field}>
                      <label htmlFor={`meta-${field}`} className="block text-sm font-semibold text-gray-700 mb-1">{t(label)}</label>
                      <input
                        id={`meta-${field}`}
                        type={type}
                        dir="ltr"
                        autoComplete="off"
                        value={metaFields[field]}
                        onChange={(e) => setMetaFields(prev => ({ ...prev, [field]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm font-mono"
                        disabled={isCreating}
                      />
                      {help && <p className="text-xs text-gray-500 mt-1">{t(help)}</p>}
                    </div>
                  ))}
                  <p className="text-xs text-amber-700">{t('conn.metaTemplateNote')}</p>
                </div>
              )}

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <label htmlFor="interactive-campaign-toggle" className="text-sm font-semibold text-gray-800 cursor-pointer">
                        Interactive Campaign
                      </label>
                    </div>
                    <p className="text-xs text-gray-600">{t('conn.interactiveHelp')}</p>
                  </div>
                  <button
                    id="interactive-campaign-toggle"
                    type="button"
                    onClick={() => setInteractiveCampaignEnabled(!interactiveCampaignEnabled)}
                    disabled={isCreating}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      interactiveCampaignEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    } ${isCreating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={interactiveCampaignEnabled}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        interactiveCampaignEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setCreateSessionModalOpen(false);
                    setNewSessionName('');
                    setNewSessionProvider('WAHA');
                    setMetaFields(EMPTY_META_FIELDS);
                    setInteractiveCampaignEnabled(false);
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200"
                  disabled={isCreating}
                >{t('common.cancel')}</button>
                <button
                  type="button"
                  onClick={async () => {
                    await createSession();
                    setCreateSessionModalOpen(false);
                  }}
                  disabled={isCreating || !newSessionName.trim()
                    || (newSessionProvider === 'META' && Object.values(metaFields).some(value => !value.trim()))}
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>{t('conn.creating')}</>
                  ) : (
                    t('conn.create')
                  )}
                </button>
              </div>
            </div>
          </div>
          </div>
        </Portal>
      )}

      {/* Meta: dados para colar no painel do app */}
      {metaWebhook && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg border border-gray-100 m-4" role="dialog" aria-labelledby="meta-webhook-title">
              <h2 id="meta-webhook-title" className="text-xl font-bold text-gray-900 mb-2">{t('conn.metaWebhookTitle')}</h2>
              <p className="text-sm text-gray-600 mb-4">{t('conn.metaWebhookSteps')}</p>
              {metaWebhook.callbackUrl ? (
                <div className="space-y-3">
                  {([
                    ['conn.metaCallbackUrl', metaWebhook.callbackUrl],
                    ['conn.metaVerifyToken', metaWebhook.verifyToken || ''],
                  ] as const).map(([label, value]) => (
                    <div key={label}>
                      <p className="text-sm font-semibold text-gray-700 mb-1">{t(label)}</p>
                      <div className="flex gap-2">
                        <code dir="ltr" className="flex-1 min-w-0 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs break-all">{value}</code>
                        <button
                          type="button"
                          onClick={() => copyText(value)}
                          className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm hover:bg-gray-200"
                        >{t('conn.copy')}</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-amber-700">{t('conn.metaNoPublicUrl')}</p>
              )}
              <div className="flex justify-end pt-6">
                <button
                  type="button"
                  onClick={() => setMetaWebhook(null)}
                  className="bg-green-600 text-white py-2 px-6 rounded-xl hover:bg-green-700 font-medium"
                >{t('conn.done')}</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal do QR Code */}
      {qrModalOpen && currentQRSession && (
        <div className="qr-modal-overlay" onClick={closeQRModal}>
          <div className="qr-modal-content bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                QR Code - {currentQRSession.displayName || currentQRSession.name} ({currentQRSession.provider})
              </h3>
              <button
                onClick={closeQRModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {currentQRSession?.qr ? (
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-4">{t('conn.qrTitle')}</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Open WhatsApp on your phone, go to <strong>Settings → Linked devices → Link a device</strong> and scan the code below
                  </p>
                  <div className="bg-white p-6 rounded-lg border inline-block shadow-lg">
                    {(() => {
                      // Para Evolution: QR vem em base64 direto
                      if (currentQRSession.provider === 'EVOLUTION' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                          />
                        );
                      }

                      // Para Quepasa: QR vem em base64
                      if (currentQRSession.provider === 'QUEPASA' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                            onError={(e) => {
                              console.error('Erro ao carregar QR Quepasa base64');
                            }}
                          />
                        );
                      }

                      // Para WAHA: QR já vem processado pelo backend em base64
                      if (currentQRSession.provider === 'WAHA' && currentQRSession.qr?.startsWith('data:image')) {
                        return (
                          <img
                            src={currentQRSession.qr}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                            onError={(e) => {
                              console.error('Erro ao carregar QR WAHA base64');
                            }}
                          />
                        );
                      }

                      // Fallback: detectar automaticamente
                      const qrSrc = currentQRSession.qr?.startsWith('data:image')
                        ? currentQRSession.qr
                        : `/api/waha/sessions/${currentQRSession.name}/auth/qr`;

                      return (
                        <img
                          src={qrSrc}
                          alt="QR Code WhatsApp"
                          className="w-64 h-64 mx-auto block border-2 border-gray-200 rounded"
                          onError={(e) => {
                            console.error('Erro ao carregar QR fallback');
                          }}
                        />
                      );
                    })()}
                  </div>
                  {currentQRSession.qrExpiresAt && (
                    <div className="mt-4">
                      <QRCountdown expiresAt={currentQRSession.qrExpiresAt} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <span className="text-gray-600">{t('conn.qrGenerating')}</span>
                  <p className="text-sm text-gray-500 mt-2">{t('conn.qrWait')}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => openQRModal(currentQRSession.name)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >{t('conn.qrRefresh')}</button>
              <button
                onClick={closeQRModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >{t('conn.close')}</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}